import { Formik, Field, ErrorMessage } from 'formik';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import CheckboxCard from 'components/Profile/CheckboxCard';
import SectionLegend from 'components/Profile/SectionLegend';
import StepShell from 'components/Profile/StepShell';
import GroupCard from './GroupCard';
import { MEDICAL_GROUPS, YES_NO, choiceBtnClass, getInitialValues, validationSchema } from './helper';
import { useOutletContext } from 'react-router-dom';
import { connect } from 'react-redux';
import { ProfileActions } from 'store/actions';

let enhancer = connect(null, { ...ProfileActions })

const Form = ({ updateMedicalHistory, medicalHistory = null, onSaved = null, inModal = false, formId = '' }) => {
  const { sectionData, mergeSection, goToNextStep }: any = useOutletContext() || {};
  const initialData = medicalHistory ?? sectionData.medical;

  const checkedIds = (obj) => Object.keys(obj).filter(id => obj[id]).map(Number)

  async function handleSubmit({ allergies, conditions, medications, medical_notes, have_no_medical_conditions }) {
    try {
      const { data } = await updateMedicalHistory({
        patient: {
          have_no_medical_conditions,
          allergy_ids: checkedIds(allergies),
          condition_ids: checkedIds(conditions),
          medication_ids: checkedIds(medications),
          medical_notes,
        },
      });

      toast.success('Medical history saved successfully');

      if (onSaved)
        onSaved(data);
      else {
        mergeSection('medical', data);
        goToNextStep('medical');
      }

    } catch (error: any) {
      toast.error(error.errors || 'Failed to save medical history. Please try again later.');
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(initialData)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, isSubmitting }) => (
        <StepShell stepId="medical" isSubmitting={isSubmitting} bodyClassName="space-y-8" inModal={inModal} formId={formId}>
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 border border-gray-200 rounded-xl p-4">
              <div className="min-w-0 flex-1">
                <p className="text-md font-bold mb-2">Any allergies or medical conditions?</p>
                <p className="text-gray-500 mt-1 text-xs">Choose No if none apply — we'll skip the rest.</p>
              </div>

              <div className="flex w-full items-center gap-2 shrink-0 sm:w-auto">
                {YES_NO.map(({ label, value }) => {
                  const isSelected = values.have_no_medical_conditions === !value;

                  return (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => setFieldValue('have_no_medical_conditions', !value)}
                      className={choiceBtnClass(isSelected)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <ErrorMessage name="have_no_medical_conditions" render={(msg) => (
              <p className="text-error text-xs mt-1 ml-1">{msg}</p>
            )} />
          </div>

          {!values.have_no_medical_conditions && (
            <>
              <div className="space-y-4">
                {MEDICAL_GROUPS.map((group) => (
                  <GroupCard
                    key={group.name}
                    {...group}
                    items={initialData?.[group.name]?.data ?? []}
                  />
                ))}
              </div>

              <div>
                <SectionLegend>Anything Else?</SectionLegend>
                <Field name="medical_notes">
                  {({ field }) => (
                    <div className="flex gap-2">
                      <input
                        {...field}
                        placeholder="Describe any other conditions, medications, or concerns..."
                        className="input input-bordered w-full"
                      />
                      {values.medical_notes && (
                        <button
                          type="button"
                          className="btn btn-square btn-ghost border-gray-300 text-black bg-white hover:bg-black hover:text-white"
                          onClick={() => setFieldValue('medical_notes', '')}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </Field>
              </div>
            </>
          )}

          <CheckboxCard
            name="reviewed"
            label="I've reviewed this and it's accurate & up to date"
            className="bg-gray-50 border-gray-200"
          />
        </StepShell>
      )}
    </Formik>
  );
};

export default enhancer(Form);
